import { CARE_TIERS, EVENTS, TIERS, type CareTierId } from "@/data/tunables";
import { CARD_SCENES, cardById, cardsForScene, type CardEffect, type CardScene } from "@/data/cards";
import { LOOT_ITEMS, SATIRE_TOASTS } from "@/data/satire";
import { draw, drawInt } from "@/systems/rng";
import { applyHeatGain, applyRepLoss } from "@/systems/meters";
import type { GameState } from "@/state/GameState";

/**
 * v0.13 "The Texture" — the event scheduler (RFC §3.1). Sim-pure,
 * Phaser-free, ONLINE-ONLY (RULED §8.5): the engine steps this once per
 * live tick; offline catch-up never calls it, so cooldowns and loot
 * lifetimes FREEZE while away.
 *
 * Determinism: every roll draws from `run.events.rng` — the scheduler's
 * OWN saved stream, isolated from the main sim rng — and channels are
 * consulted in FIXED order (satire, then care tiers in CARE_TIERS
 * order). Same state → identical spawn/expiry stream, and skipping
 * event ticks offline cannot perturb main-sim replay.
 */

export type EventTickEvent =
  | { type: "lootSpawned"; id: number; kind: "money" | "contraband"; tier: CareTierId; item: string; lifeTicks: number }
  | { type: "lootExpired"; id: number; tier: CareTierId }
  | { type: "satire"; text: string }
  | { type: "cardSpawned"; id: number; defId: string; scene: CardScene; crisis: boolean }
  | { type: "cardResolved"; id: number; defId: string; scene: CardScene; expired: boolean };

const SATIRE_CHANNEL = "satire";

/** Fail-loud value derivation — a non-finite tier payout must never
 * mint NaN loot (it would brick the next autosave's deep validation). */
function lootValue(tier: CareTierId): number {
  const base = TIERS[tier].promotionPayout;
  if (!Number.isFinite(base)) {
    throw new Error(`stepEvents: TIERS.${tier}.promotionPayout is not finite — loot value undefined`);
  }
  return Math.round(base * EVENTS.loot.moneyValueMult);
}
const lootChannel = (tier: CareTierId): string => `loot:${tier}`;

/** v0.14: is a scene frozen by an open card? (pause-the-tier — RFC §3.2). */
export function scenePaused(state: GameState, scene: CardScene): boolean {
  return state.run.events.cards.some((c) => c.scene === scene);
}

/** One live tick of the scheduler. Mutates run.events; returns events. */
export function stepEvents(state: GameState): EventTickEvent[] {
  const ev = state.run.events;
  const out: EventTickEvent[] = [];

  // 1. Age active loot; cull expiries (legal-empty, not an error).
  for (const loot of ev.active) loot.lifeLeft -= 1;
  const expired = ev.active.filter((l) => l.lifeLeft <= 0);
  for (const loot of expired) {
    out.push({ type: "lootExpired", id: loot.id, tier: loot.tier });
  }
  ev.active = ev.active.filter((l) => l.lifeLeft > 0);

  // 1b. Age ONLY the FRONT card (the one on screen). Queued cards hold at
  //     full lifetime and their tier stays paused-and-waiting until they
  //     surface — a hidden card must never bleed or expire unseen (v0.14
  //     review, codex: passive-on-expire is only fair if you SAW it drain).
  const front = ev.cards[0];
  if (front) {
    front.ticksLeft -= 1;
    const def = cardById(front.defId);
    if (def.bleed) applyCardEffects(state, def.bleed, front.scene);
  }
  const expiredCards = ev.cards.filter((c) => c.ticksLeft <= 0);
  for (const card of expiredCards) {
    const def = cardById(card.defId);
    applyCardEffects(state, def.passive, card.scene);
    out.push({ type: "cardResolved", id: card.id, defId: card.defId, scene: card.scene, expired: true });
  }
  ev.cards = ev.cards.filter((c) => c.ticksLeft > 0);

  // 2. Cool every channel one tick.
  for (const key of Object.keys(ev.cooldownLeft)) {
    if (ev.cooldownLeft[key]! > 0) ev.cooldownLeft[key]! -= 1;
  }
  // Card-expiry cooldowns anchor AFTER the decrement (0.13.1's loot
  // lesson applied consistently — setting them in step 1 loses a tick).
  for (const card of expiredCards) ev.cooldownLeft[`card:${card.scene}`] = EVENTS.card.cooldownTicks;
  // Expiry cooldowns anchor AFTER the decrement pass — setting them in
  // step 1 lost a tick to step 2 in the same call (149 ≠ 150; bot leg).
  for (const loot of expired) {
    ev.cooldownLeft[`loot:${loot.tier}`] = EVENTS.loot.cooldownTicks;
  }

  // 3. Roll channels in FIXED order. Satire first (global)…
  if ((ev.cooldownLeft[SATIRE_CHANNEL] ?? 0) <= 0 && draw(ev.rng) < EVENTS.satire.weight) {
    if (ev.satireDeck.length === 0) {
      // Reshuffle the full deck (seeded Fisher–Yates via the event rng).
      const deck = SATIRE_TOASTS.map((_, i) => i);
      for (let i = deck.length - 1; i > 0; i--) {
        const j = drawInt(ev.rng, i + 1);
        [deck[i], deck[j]] = [deck[j]!, deck[i]!];
      }
      ev.satireDeck = deck;
    }
    const idx = ev.satireDeck.shift()!;
    out.push({ type: "satire", text: SATIRE_TOASTS[idx]! });
    ev.cooldownLeft[SATIRE_CHANNEL] = EVENTS.satire.cooldownTicks;
  }

  // …then one loot channel per OPEN care tier, at most one active each.
  for (const tier of CARE_TIERS) {
    if (!state.run.availableTiers.includes(tier)) continue;
    if (ev.active.some((l) => l.tier === tier)) continue;
    const ch = lootChannel(tier);
    if ((ev.cooldownLeft[ch] ?? 0) > 0) continue;
    if (draw(ev.rng) >= EVENTS.loot.weight) continue;
    const contraband = draw(ev.rng) < EVENTS.loot.contrabandChance;
    const pool = contraband ? LOOT_ITEMS.contraband : LOOT_ITEMS.money;
    const item = pool[drawInt(ev.rng, pool.length)]!;
    ev.seq += 1;
    const spawned = {
      id: ev.seq,
      kind: contraband ? ("contraband" as const) : ("money" as const),
      tier,
      item,
      value: lootValue(tier),
      lifeLeft: EVENTS.loot.lifetimeTicks,
    };
    ev.active.push(spawned);
    // Cooldown anchors at RESOLVE (collect/expiry), not spawn — the M5
    // chart's pending→cooldown edge is the truth (v0.13 review, codex+
    // swarm converged: spawn-anchoring made cadence depend on pickup
    // delay and the picture lied).
    out.push({ type: "lootSpawned", id: spawned.id, kind: spawned.kind, tier, item, lifeTicks: spawned.lifeLeft });
  }

  // …then one CARD channel per SCENE (fixed order), at most one open card
  // each. Cards roll after loot; same isolated event rng, same order.
  for (const scene of CARD_SCENES) {
    // Office is always live (the funnel exists from tick 1); every other
    // scene — including alumni — must be UNLOCKED before its card rolls
    // (bot leg P2: a fresh run was rolling card:alumni pre-network).
    if (scene !== "office" && !state.run.availableTiers.includes(scene as never)) continue;
    if (ev.cards.some((c) => c.scene === scene)) continue;
    const ch = `card:${scene}`;
    if ((ev.cooldownLeft[ch] ?? 0) > 0) continue;
    if (draw(ev.rng) >= EVENTS.card.weight) continue;
    const pool = cardsForScene(scene);
    if (pool.length === 0) continue;
    const def = pool[drawInt(ev.rng, pool.length)]!;
    ev.seq += 1;
    ev.cards.push({ id: ev.seq, defId: def.id, scene, ticksLeft: EVENTS.card.lifetimeTicks });
    out.push({ type: "cardSpawned", id: ev.seq, defId: def.id, scene, crisis: def.crisis === true });
  }

  return out;
}

/** Apply a card's effect list. Pure-ish (mutates run/account like every
 * action); rolls draw from the ISOLATED event rng so card resolution
 * stays deterministic and off the main sim stream. */
export function applyCardEffects(state: GameState, effects: readonly CardEffect[], scene: CardScene): void {
  for (const e of effects) {
    switch (e.type) {
      case "funding":
        state.run.funding += e.amount;
        break;
      case "heat":
        applyHeatGain(state, e.amount);
        break;
      case "dea":
        state.run.dea = Math.max(0, state.run.dea + e.amount);
        break;
      case "rep":
        if (e.amount < 0) applyRepLoss(state, -e.amount);
        else state.run.reputation += e.amount;
        break;
      case "sk":
        state.account.streetKnowledge += e.amount;
        break;
      case "losePatient": {
        if (scene === "office" || scene === "alumni") break; // no census to lose
        const idx = state.run.patients.findIndex((p) => p.tier === scene);
        if (idx >= 0) state.run.patients.splice(idx, 1);
        break;
      }
      case "killPatient": {
        // A card death is a REAL death (bot leg P1): count it so the Death
        // Letter + payout scaling see it, not a silent census decrement.
        if (scene === "office" || scene === "alumni") break;
        const idx = state.run.patients.findIndex((p) => p.tier === scene);
        if (idx >= 0) {
          state.run.patients.splice(idx, 1);
          state.run.deathsEver += 1;
          state.run.deathsPending += 1;
        }
        break;
      }
      case "roll":
        applyCardEffects(state, draw(state.run.events.rng) < e.chance ? e.then : e.else, scene);
        break;
    }
  }
}
